import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Club() {
    const [tiers, setTiers] = useState([]);
    const [selected, setSelected] = useState("abonne");
    const [email, setEmail] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);

    useEffect(() => {
        api.get("/club/tiers").then(r => setTiers(r.data)).catch(() => {});
        const remembered = localStorage.getItem("cp_email");
        if (remembered) {
            setEmail(remembered);
            api.get(`/club/status/${encodeURIComponent(remembered)}`)
                .then(r => setStatus(r.data))
                .catch(() => {});
        }
    }, []);

    async function handleJoin(tierSlug) {
        if (!email) { setErr("Merci d'indiquer votre email avant de rejoindre le Cercle."); return; }
        setLoading(true); setErr("");
        localStorage.setItem("cp_email", email);
        try {
            const r = await api.post(`/checkout/session`, {
                product_type: "club",
                product_slug: tierSlug,
                email,
                origin_url: window.location.origin,
            });
            window.location.href = r.data.url;
        } catch (e) {
            setErr(e?.response?.data?.detail || "Paiement impossible — réessayez.");
            setLoading(false);
        }
    }

    return (
        <div data-testid="club-page" className="bg-cp-ivoire">
            <section className="relative min-h-[80vh] bg-cp-encre text-cp-ivoire overflow-hidden cp-grain">
                <img src={`${BACKEND}/api/static/img/mon_club_key.jpg`} alt=""
                     className="absolute inset-0 w-full h-full object-cover cp-kenburns-slow cp-photo-cinematic"/>
                <div className="absolute inset-0 cp-hero-overlay"/>
                <div className="relative z-10 min-h-[80vh] max-w-[1600px] mx-auto px-5 sm:px-6 md:px-12 pt-32 md:pt-40 pb-20 flex flex-col justify-end">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
                        <p className="cp-tiny text-cp-sable mb-8">Le Cercle · Côte Perdue Club</p>
                        <h1 className="cp-serif text-[56px] sm:text-[88px] md:text-[140px] lg:text-[184px] leading-[0.88] tracking-[-0.03em] max-w-5xl">
                            Le <em className="cp-display-italic text-cp-sable">Cercle.</em><br />
                            Là où la côte <em className="cp-display-italic text-cp-sable">s'ouvre.</em>
                        </h1>
                        <p className="cp-serif italic text-base md:text-2xl font-light text-cp-ivoire/90 leading-[1.5] mt-8 max-w-3xl">
                            Un club. Deux formules. Une seule intention : offrir à ses membres ce que nous gardons par ailleurs pour nous. Plans exclusifs, invitations, réductions, carnets noirs.
                        </p>
                    </motion.div>
                </div>
            </section>

            {status?.active && (
                <section className="bg-cp-foret text-cp-ivoire py-10 md:py-14" data-testid="club-active-banner">
                    <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div>
                            <p className="cp-tiny text-cp-sable mb-2">Bienvenue au Cercle</p>
                            <p className="cp-serif text-2xl md:text-3xl">
                                <em className="cp-display-italic">{status.tier_title}</em> · Membre <em className="cp-display-italic">№ {status.member_number}</em>
                            </p>
                            <p className="cp-serif italic text-cp-ivoire/75 mt-2 text-sm">Valide jusqu'au {new Date(status.expires_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}</p>
                        </div>
                    </div>
                </section>
            )}

            <section className="py-24 md:py-32 max-w-[1400px] mx-auto px-5 sm:px-6 md:px-12">
                <div className="max-w-md mb-12 md:mb-16">
                    <p className="cp-tiny text-cp-terre mb-3">Votre email</p>
                    <input type="email" placeholder="votre@email.com" value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 bg-cp-craie border border-cp-border focus:border-cp-foret outline-none cp-serif text-lg"
                        data-testid="club-email-input"/>
                    {err && <p className="cp-tiny text-cp-terre mt-3" data-testid="club-error">{err}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
                    {tiers.map((t) => {
                        const isFondateur = t.slug === "cercle-fondateur";
                        const full = t.remaining === 0;
                        return (
                            <motion.div key={t.slug}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7 }}
                                data-testid={`club-tier-${t.slug}`}
                                onMouseEnter={() => setSelected(t.slug)}
                                className={`p-10 md:p-14 border ${selected === t.slug ? "border-cp-foret bg-cp-craie" : "border-cp-border bg-cp-ivoire"} ${isFondateur ? "lg:py-20 shadow-[0_0_0_1px_rgba(30,42,37,0.15)] relative" : ""} transition-all`}>
                                {isFondateur && (
                                    <span className="absolute -top-4 left-10 cp-tiny bg-cp-terre text-cp-ivoire px-4 py-2">Limité · 100 places</span>
                                )}
                                <p className="cp-tiny text-cp-terre mb-4">{isFondateur ? "Formule Fondateur" : "Formule Abonné"}</p>
                                <h2 className="cp-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-cp-encre mb-2 tracking-[-0.02em]">
                                    <em className="cp-display-italic">{t.title}</em>
                                </h2>
                                <p className="cp-serif italic text-3xl text-cp-foret mb-8">{t.price}€ <span className="text-lg text-cp-gris">/ an</span></p>
                                {isFondateur && typeof t.remaining === "number" && (
                                    <p className="cp-tiny text-cp-terre mb-6" data-testid="cercle-remaining">{t.remaining} places restantes sur 100</p>
                                )}
                                <ul className="space-y-4 mb-10">
                                    {t.perks.map((p, i) => (
                                        <li key={i} className="flex items-start gap-3 cp-serif text-lg">
                                            <span className="cp-serif italic text-cp-terre leading-none shrink-0 mt-1">·</span>
                                            <span>{p}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button onClick={() => handleJoin(t.slug)} disabled={loading || full || status?.active}
                                    className="cp-btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid={`club-join-${t.slug}`}>
                                    {status?.active ? "Déjà membre" : full ? "Complet" : loading ? "Un instant…" : `Rejoindre — ${t.price}€`}
                                </button>
                                <p className="cp-tiny text-cp-gris/70 mt-4 text-center">Paiement Stripe · 365 jours · aucun renouvellement automatique</p>
                            </motion.div>
                        );
                    })}
                </div>
            </section>

            <section className="bg-cp-craie py-20 md:py-28">
                <div className="max-w-[1000px] mx-auto px-5 sm:px-6 md:px-12">
                    <p className="cp-tiny text-cp-terre mb-6">Questions fréquentes</p>
                    <div className="space-y-10 cp-serif text-lg md:text-xl leading-[1.55] text-cp-encre">
                        <div>
                            <h3 className="cp-serif italic text-2xl text-cp-foret mb-2">Ça engage à quoi exactement ?</h3>
                            <p>Rien. L'abonnement ne se renouvelle pas tout seul. Vous payez 365 jours — c'est tout.</p>
                        </div>
                        <div>
                            <h3 className="cp-serif italic text-2xl text-cp-foret mb-2">Et si je ne vis pas à Paris ?</h3>
                            <p>Tout le contenu digital (lettre, plans, guides) vous est envoyé par email. Les avantages physiques (goodies Fondateur, dîner d'ouverture) sont livrés ou gardés jusqu'à votre venue.</p>
                        </div>
                        <div>
                            <h3 className="cp-serif italic text-2xl text-cp-foret mb-2">Le Cercle Fondateur — c'est vraiment limité ?</h3>
                            <p>Cent places. Numérotées. C'est un choix. Une fois fermé, l'accès Fondateur disparaît — les places se rouvriront seulement en cas de départ.</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
